// ============================================================
// DEALIN MVP - Main Layout Component
// ============================================================

import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Headset, ArrowUpRight } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { ToastContainer } from '@/components/ui/Toast';
import { trackPageView } from '@/components/analytics/GoogleAnalytics';
import { useAuth } from '@/hooks/useAuth';

interface LayoutProps {
  children?: React.ReactNode;
}

const SUPPORT_DESK_URL = 'https://dealin.atlassian.net/servicedesk/customer/portal/1';

const SupportFab = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <a
      href={SUPPORT_DESK_URL}
      target="_blank"
      rel="noreferrer"
      aria-label="Apri il supporto Dealin"
      className="fixed bottom-5 right-5 z-50 group"
    >
      <div className="flex items-center gap-3 rounded-full border border-slate-200/80 bg-white/95 px-4 py-3 shadow-[0_18px_45px_rgba(15,23,42,0.14)] backdrop-blur-md transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-[0_22px_55px_rgba(15,23,42,0.18)]">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-white shadow-sm">
          <Headset size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Supporto
          </div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            Apri ticket
            <ArrowUpRight size={15} className="text-slate-400 transition-colors group-hover:text-slate-700" />
          </div>
        </div>
      </div>
    </a>
  );
};

export const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-clip">
      <Header />
      
      <main className="flex-1 w-full">
        {children ?? <Outlet />}
      </main>
      
      <Footer />
      <ToastContainer />
      <SupportFab />
    </div>
  );
};

/**
 * Layout without footer (for full-height pages like Chat)
 */
export const LayoutNoFooter = ({ children }: LayoutProps) => {
  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-clip">
      <Header />
      
      <main className="flex-1 w-full">
        {children ?? <Outlet />}
      </main>
      
      <ToastContainer />
      <SupportFab />
    </div>
  );
};

/**
 * Minimal layout (for auth pages)
 */
export const LayoutMinimal = ({ children }: LayoutProps) => {
  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-clip">
      <div className="flex-1 flex items-center justify-center p-4">
        {children ?? <Outlet />}
      </div>
      <ToastContainer />
    </div>
  );
};

